
import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { authPost } from './auth-fetch.ts';
import { clearStashedGrant } from './password-grant-storage.ts';
import {
  fetchSession,
  hasAnyRole,
  isOnboardingSession,
  isPlatformSession,
  isSetupIncompleteSession,
  SESSION_QUERY_KEY,
  type OnboardingSession,
  type PlatformSession,
  type Role,
  type Session,
  type SessionOrNull,
  type SessionResult,
  type SetupIncompleteSession,
} from './session.ts';

export type SessionState =
  | { status: 'loading'; session: null }
  | { status: 'unauthenticated'; session: null }
  | { status: 'platform'; session: null; platform: PlatformSession }
  | { status: 'onboarding'; session: null; onboarding: OnboardingSession }
  | { status: 'setup-incomplete'; session: null; setupIncomplete: SetupIncompleteSession }
  | { status: 'authenticated'; session: Session };

const SessionContext = createContext<SessionState | undefined>(undefined);

function deriveState(isLoading: boolean, data: SessionResult | undefined): SessionState {
  if (isLoading) return { status: 'loading', session: null };
  if (!data) return { status: 'unauthenticated', session: null };
  if (isPlatformSession(data)) return { status: 'platform', session: null, platform: data };
  if (isOnboardingSession(data)) return { status: 'onboarding', session: null, onboarding: data };
  if (isSetupIncompleteSession(data)) {
    return { status: 'setup-incomplete', session: null, setupIncomplete: data };
  }
  return { status: 'authenticated', session: data };
}

export interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchSession(signal),
    staleTime: 60_000,
    retry: false,
  });

  const state = deriveState(query.isLoading, query.data);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (ctx === undefined) {
    throw new Error('useSession must be used within a <SessionProvider>.');
  }
  return ctx;
}

export function useCurrentUser(): SessionOrNull {
  return useSession().session;
}

export function useHasRole(...roles: Role[]): boolean {
  const { session } = useSession();
  return hasAnyRole(session, ...roles);
}

export function invalidateSession(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
}

export function useLogout(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    try {
      await authPost('/auth/logout', 'logout');
    } finally {
      clearStashedGrant();
      await invalidateSession(queryClient);
      queryClient.clear();
    }
  }, [queryClient]);
}
